import { useCallback, useEffect } from 'react';

import { useAuthStore } from '../store/auth';
import { useCompanyStore } from '../store/company';
import { authApi } from '../api/auth';
import { clearRememberedRoutes } from '../lib/postLoginPath';
import api from '../lib/axios';

export const useAuth = () => {
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();

  const login = async (email, password) => {
    try {
      const result = await authStore.login(email, password);

      // Se 2FA for requerido, não tenta carregar dados nem setar token agora
      if (result?.requiresTwoFactor) {
        return { requiresTwoFactor: true, tempToken: result.tempToken, message: result.message };
      }

      // Email não verificado
      if (result?.emailNotVerified) {
        return { emailNotVerified: true, error: result.error };
      }

      // Se login falhou, propaga erro padronizado
      if (!result?.success) {
        return {
          success: false,
          error: result?.error || 'Credenciais inválidas',
          status: result?.status,
        };
      }

      // Sucesso: carregar dados do usuário
      await loadUserData();
      return { success: true, access_token: result.access_token, user: result.user };
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    clearRememberedRoutes(authStore.user?.id);
    authStore.logout();
    companyStore.clearCompany();
    window.location.href = '/login';
  };

  const loadUserData = useCallback(async () => {
    try {
      const response = await authApi.me();
      // A resposta tem a estrutura { user: { ... } }
      const userData = response.data.user || response.data;

      // Atualizar dados do usuário
      authStore.setUser(userData);

      // Atualizar empresas do usuário
      const companies = userData.companies || [];
      companyStore.setCompanies(companies);

      // Selecionar automaticamente a primeira empresa se não houver empresa selecionada
      const currentCompanyId = companyStore.companyId;
      if (!currentCompanyId && companies.length > 0) {
        const firstCompany = companies[0];
        companyStore.setCompanyId(firstCompany.id);
        companyStore.setCompany(firstCompany);
      } else if (currentCompanyId) {
        // Atualizar a empresa atual com os novos dados (incluindo can_view_company_reports)
        const updatedCompany = companies.find(c => c.id === currentCompanyId);
        if (updatedCompany) {
          companyStore.setCompany(updatedCompany);
        }
      }

      return userData;
    } catch (error) {
      throw error;
    }
  }, [authStore, companyStore]);

  const refreshUserData = async () => {
    if (authStore.isAuthenticated()) {
      try {
        await loadUserData();
      } catch (error) {
        // Se falhar ao recarregar dados, fazer logout
        logout();
      }
    }
  };

  const verifyTwoFactor = async (code, tempToken) => {
    try {
      const response = await api.post(
        '/auth/2fa/verify',
        { code, temp_token: tempToken },
        { timeout: 25000 },
      );

      const { access_token, user: userData } = response.data;
      authStore.setToken(access_token);
      authStore.setUser(userData);

      // Carregar dados completos do usuário após 2FA
      await loadUserData();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Código inválido',
      };
    }
  };

  const resendVerificationEmail = async email => {
    try {
      await api.post('/auth/email/resend', { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao reenviar e-mail',
      };
    }
  };

  // Carregar dados do usuário automaticamente se houver token
  useEffect(() => {
    if (authStore.token && !authStore.user) {
      loadUserData().catch(() => {
        // Se falhar, limpar token
        authStore.clearToken();
      });
    }
  }, [authStore.token, authStore.user, authStore, loadUserData]);

  return {
    user: authStore.user,
    token: authStore.token,
    isAuthenticated: authStore.isAuthenticated(),
    login,
    logout,
    loadUserData,
    refreshUserData,
    verifyTwoFactor,
    resendVerificationEmail,
    loading: !authStore.token && !authStore.user, // Loading inicial
  };
};
